import { isNud } from '@/utilities/typing';
import { dateYmd, dateYmdForInputDate } from '@/utilities/date-format';
import School from '@/models/entities/school';
import Event from '@/models/entities/event';
import ScheduleEquipment from '@/models/entities/schedule-equipment';
import ScheduleFile from '@/models/entities/schedule-file';
import ScheduleHistory from '@/models/entities/schedule-history';
import ScheduleMemo from '@/models/entities/schedule-memo';
import SchedulePhotographer from '@/models/entities/schedule-photographer';
import ScheduleType from '@/models/enums/schedule/schedule-type';
import HasSeveralDays from '@/models/enums/schedule/has-several-days';
import IsUnfixed from '@/models/enums/schedule/is-unfixed';
import ScheduleStatus from '@/models/enums/schedule/schedule-status';

/**
 * スケジュールトラン 予備日 エンティティ
 */
class SpareSchedule {
    schedule_id;
    school;
    event;
    schedule_status;
    schedule_name;
    schedule_type;
    schedule_date;
    hour_from;
    minute_from;
    hour_to;
    minute_to;
    is_unfixed;
    unfixed_type;
    has_several_days;
    first_schedule;
    end_date;
    end_hour;
    end_minute;
    rain_type;
    spare_schedule;
    checked_date;
    photographer_number;
    transportation;
    schedule_place;
    contact_person;
    contact_tel;
    note;
    has_first_confirm_alert;
    has_last_confirm_alert;
    has_spare_schedule_alert;
    has_unselected_photographer_schedule_alert;
    has_no_contact_photographer_schedule_alert;
    has_status_list_alert;

    files;
    cars;
    equipments;
    photographers;
    memos;
    histories;


    constructor(properties) {
        if (isNud(properties)) {
            properties = {};
        }

        this.schedule_id = properties.schedule_id;
        this.school = new School(properties.school);
        this.event = new Event(properties.event);
        this.schedule_name = properties.schedule_name;
        this.schedule_status = properties.schedule_status;
        this.schedule_type = properties.schedule_type;
        this.schedule_date = properties.schedule_date;
        this.hour_from = properties.hour_from;
        this.minute_from = properties.minute_from;
        this.hour_to = properties.hour_to;
        this.minute_to = properties.minute_to;
        this.is_unfixed = properties.is_unfixed ?? IsUnfixed.FIXED;
        this.unfixed_type = properties.unfixed_type;
        this.has_several_days = properties.has_several_days ?? HasSeveralDays.NO;
        this.first_schedule = properties.first_schedule;
        this.end_date = properties.end_date;
        this.end_hour = properties.end_hour;
        this.end_minute = properties.end_minute;
        this.rain_type = properties.rain_type;
        this.spare_schedule = properties.spare_schedule;
        this.checked_date = properties.checked_date;
        this.photographer_number = properties.photographer_number ?? 0;
        this.transportation = properties.transportation;
        this.schedule_place = properties.schedule_place;
        this.contact_person = properties.contact_person;
        this.contact_tel = properties.contact_tel;
        this.note = properties.note;
        this.has_first_confirm_alert = properties.has_first_confirm_alert;
        this.has_last_confirm_alert = properties.has_last_confirm_alert;
        this.has_spare_schedule_alert = properties.has_spare_schedule_alert;
        this.has_unselected_photographer_schedule_alert	 = properties.has_unselected_photographer_schedule_alert;
        this.has_no_contact_photographer_schedule_alert = properties.has_no_contact_photographer_schedule_alert;
        this.has_status_list_alert = properties.has_status_list_alert;

        this.cars = [];
        if ('cars' in properties) {
            for (let car_id of properties.cars) {
                this.cars.push(parseInt(car_id, 10));
            }
        }

        this.files = [];
        if ('files' in properties) {
            for (let file of properties.files) {
            this.files.push(new ScheduleFile(file));
            }
        }

        this.equipments = [];
        if ('equipments' in properties) {
            for (let equipment of properties.equipments) {
            this.equipments.push(new ScheduleEquipment(equipment));
            }
        }
        this.photographers = [];
        if ('photographers' in properties) {
            for (let photographer of properties.photographers) {
                this.photographers.push(new SchedulePhotographer(photographer));
            }
        }
        this.memos = [];
        if ('memos' in properties) {
            for (let memo of properties.memos) {
            this.memos.push(new ScheduleMemo(memo));
            }
        }
        this.histories = [];
        if ('histories' in properties) {
            for (let history of properties.histories) {
            this.histories.push(new ScheduleHistory(history));
            }
        }
    }

    /**
     * getter
     */
    get schedule_date_display() {
        return dateYmd(this.schedule_date);
    }
    get schedule_date_for_input() {
        return dateYmdForInputDate(this.schedule_date);
    }
    get checked_date_for_input() {
        return dateYmdForInputDate(this.checked_date);
    }
    get end_date_for_input() {
        return dateYmdForInputDate(this.end_date);
    }
    get schedule_photographer_display() {
        let photographers = '';

            let shortened_names = [];
            let extra = [];

            this.photographers.forEach((schedule_photographer, index) => {
                if (index <= 2) {
                    shortened_names.push(`(${isNud(schedule_photographer.photographer.shortened_name) ? '未選択' : schedule_photographer.photographer.shortened_name})`);
                } else {
                    extra.push(`${schedule_photographer.photographer.shortened_name}`);
                }
            })

            photographers = shortened_names.join('');
            if (extra.length) {
                photographers = photographers + ` 他${extra.length}`;
            }

        return photographers;
    }
    get schedule_name_display() {
        let school_shortened_name = this.school.shortened_name ? this.school.shortened_name : '';
        let schedule_name = isNud(this.schedule_name) ? this.event.event_name : this.schedule_name;

        return `${school_shortened_name} ${schedule_name}`;
    }
    get schedule_time() {
        let minute_from = this.minute_from === 0 ? '00' : this.minute_from;
        let minute_to = this.minute_to === 0 ? '00' : this.minute_to;

        return `${this.hour_from}:${minute_from} 〜 ${this.hour_to}:${minute_to}`;
    }
    get schedule_icon() {
        if (this.has_several_days === HasSeveralDays.YES) {
            if (this.first_schedule && 'schedule_id' in this.first_schedule) {
                return 'bi-align-center';

            } else {
                return 'bi-align-start';
            }
        } else if (this.schedule_type === ScheduleType.SHOOTING) {
            return 'bi-camera2';

        } else if (this.schedule_type === ScheduleType.EVENT_SHOOTING) {
            return 'bi-balloon-fill';

        } else if (this.schedule_type === ScheduleType.MEETING) {
            return 'bi-chat-dots';

        } else if (this.schedule_type === ScheduleType.SPARE) {
            return 'bi-umbrella-fill';

        } else if (this.schedule_type === ScheduleType.OTHERS) {
            return 'bi-box-seam';
        }

        return ''
    }

    get schedule_status_display() {
        return ScheduleStatus.get(this.schedule_status);
    }

    /**
     * setter
     */
    set schedule_date_for_input(value) {
        return this.schedule_date = value;
    }

    set checked_date_for_input(value) {
        return this.checked_date = value;
    }
    set end_date_for_input(value) {
        return this.end_date = value;
    }


}

export default SpareSchedule;
